import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { enroll } from "@/lib/api";
import { Loader2, ArrowRight, CheckCircle2 } from "lucide-react";

export const EnrollDialog = ({ open, onOpenChange }) => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [smsConsent, setSmsConsent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [zoomUrl, setZoomUrl] = useState("");

  const reset = () => {
    setName(""); setEmail(""); setPhone(""); setSmsConsent(false);
    setLoading(false); setDone(false); setZoomUrl("");
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !email.trim()) {
      toast.error("Please enter your name and email.");
      return;
    }
    if (smsConsent && !phone.trim()) {
      toast.error("Please enter your mobile number to receive text reminders.");
      return;
    }
    setLoading(true);
    try {
      // Only send phone when the user has explicitly consented
      const res = await enroll(name.trim(), email.trim(), smsConsent ? phone.trim() : "");
      setZoomUrl(res.zoom_url);
      setDone(true);
      toast.success("Your seat is saved. See you Thursday at 7PM EST!");
      setTimeout(() => {
        window.open(res.zoom_url, "_blank", "noopener");
      }, 900);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong. Try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        onOpenChange(v);
        if (!v) setTimeout(reset, 300);
      }}
    >
      <DialogContent
        data-testid="enroll-dialog"
        className="border-gold/25 bg-ink-elevated text-cream sm:max-w-md"
      >
        <DialogHeader>
          <DialogTitle className="font-serif text-3xl font-semibold tracking-tight text-cream">
            {done ? "You're in." : "Save Your Seat"}
          </DialogTitle>
          <p className="pt-1 font-sans text-sm text-cream/60">
            Free live class · Thursdays @ 7PM EST
          </p>
          <DialogDescription className="sr-only">
            Enter your name and email to reserve your free seat for the #AskTasha live webinar. You'll be redirected to Zoom to confirm.
          </DialogDescription>
        </DialogHeader>

        {done ? (
          <div className="flex flex-col items-center gap-4 py-6 text-center" data-testid="enroll-success">
            <CheckCircle2 className="text-gold" size={54} />
            <p className="font-sans text-sm text-cream/70">
              We're opening your Zoom registration now. If it didn't open,
              use the button below.
            </p>
            <a
              href={zoomUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="enroll-zoom-link"
              className="btn-gold w-full"
            >
              Open Zoom Registration <ArrowRight size={16} />
            </a>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-5 pt-2" data-testid="enroll-form">
            <div className="space-y-2">
              <Label htmlFor="enroll-name" className="overline">Full Name</Label>
              <Input
                id="enroll-name"
                data-testid="enroll-name-input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Natasha Mays"
                className="border-cream/15 bg-ink text-cream placeholder:text-cream/30 focus-visible:ring-gold"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="enroll-email" className="overline">Email Address</Label>
              <Input
                id="enroll-email"
                data-testid="enroll-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@email.com"
                className="border-cream/15 bg-ink text-cream placeholder:text-cream/30 focus-visible:ring-gold"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="enroll-phone" className="overline">
                Mobile Phone <span className="text-cream/40 normal-case tracking-normal">(optional)</span>
              </Label>
              <Input
                id="enroll-phone"
                data-testid="enroll-phone-input"
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="+1 555 000 1234"
                className="border-cream/15 bg-ink text-cream placeholder:text-cream/30 focus-visible:ring-gold"
              />
            </div>
            <div className="flex items-start gap-3 rounded-lg border border-cream/10 bg-ink/40 p-3" data-testid="sms-consent-block">
              <Checkbox
                id="enroll-sms-consent"
                data-testid="enroll-sms-consent"
                checked={smsConsent}
                onCheckedChange={(v) => setSmsConsent(!!v)}
                className="mt-0.5 border-cream/30 data-[state=checked]:border-gold data-[state=checked]:bg-gold data-[state=checked]:text-ink"
              />
              <label
                htmlFor="enroll-sms-consent"
                className="cursor-pointer select-none font-sans text-[12px] leading-relaxed text-cream/70"
              >
                Yes, send me class reminder texts from #AskTasha. About 1–2 msgs per class
                week. Msg &amp; data rates may apply. Reply HELP for help, STOP to opt out.{" "}
                <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-gold underline-offset-2 hover:underline">
                  Privacy Policy
                </a>{" "}
                ·{" "}
                <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-gold underline-offset-2 hover:underline">
                  Terms
                </a>
              </label>
            </div>
            <button
              type="submit"
              disabled={loading}
              data-testid="enroll-submit-btn"
              className="btn-gold w-full disabled:opacity-60"
            >
              {loading ? (
                <><Loader2 className="animate-spin" size={16} /> Saving…</>
              ) : (
                <>Reserve My Free Seat <ArrowRight size={16} /></>
              )}
            </button>
            <p className="text-center font-sans text-xs text-cream/40">
              We'll redirect you to Zoom to confirm your spot.
            </p>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default EnrollDialog;
